import { Select, SelectProps, Space, Tooltip, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useCompanyOwnerId from '../../hooks/adminPage/useCompanyOwnerId';
import { CustomizedDetailedUser } from '../../hooks/useUserById';
import { roles, userRoles, userRolesMap } from '../../model/types';
import { useCurrentUserStore } from '../../store/RootStoreContext';
import { isEndUser } from '../../utils/accountHelper';
import RoleChangeWarningModal, { RoleChangeWarningModalRef } from './RoleChangeWarningModal';

interface UserRoleSelectProps {
    role: roles;
    userInfo: CustomizedDetailedUser;
    onChange?: (role: roles) => void;
    disabled?: boolean;
}

function UserRoleSelect({ onChange, role, userInfo, disabled = false }: UserRoleSelectProps) {
    const { t } = useTranslation('people');
    const { t: tForms } = useTranslation('forms');
    const { id: currentUserId, role: currentUserRole } = useCurrentUserStore();
    const warningModalRef = useRef<RoleChangeWarningModalRef>(null);
    const { data: ownerId } = useCompanyOwnerId();
    if (!userInfo || !currentUserRole) return null;

    const isCurrentUserOwner = currentUserId === ownerId;

    const isUserEndUser = isEndUser(userInfo?.role?.authority);

    const isRoleSelectDisabled = disabled || isUserEndUser || userInfo?.id === currentUserId || !isCurrentUserOwner;

    const roleOptions = Object.keys(userRoles)
        .filter((role) => role !== userRolesMap.ROLE_END_USER)
        .map((role: roles) => ({
            label: t(`roles.${role}`),
            value: role
        }));

    const handleOnSelect: SelectProps<roles>['onSelect'] = (roleToChange) => {
        if (roleToChange === role) return;

        // if (isAdmin(roleToChange) ||isAgent(roleToChange) || isEndUser(roleToChange)) {
        warningModalRef.current.open(roleToChange);
        // }
    };

    return (
        <>
            <RoleChangeWarningModal ref={warningModalRef} onOk={onChange} />
            <Space.Compact block size="small" style={{ alignItems: 'center' }}>
                <Typography.Text type="secondary" style={{ width: '15%' }}>
                    {tForms('form.role.label')}
                </Typography.Text>
                <Tooltip
                    title={
                        isUserEndUser
                            ? t('userRoleSelectTooltipTitle.endUser')
                            : isCurrentUserOwner
                            ? ''
                            : t('userRoleSelectTooltipTitle.user')
                    }
                >
                    <Select
                        bordered={!isRoleSelectDisabled}
                        className="full-width"
                        disabled={isRoleSelectDisabled}
                        options={roleOptions}
                        size="middle"
                        value={role === 'ROLE_END_USER' ? t(`roles.${role}`) : role}
                        onSelect={handleOnSelect}
                    />
                </Tooltip>
            </Space.Compact>
        </>
    );
}

export default observer(UserRoleSelect);
